import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { MODAL_BORDER_RADIUS } from '../../utils/dateUtils';

/**
 * Modal for editing a calendar item
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Handler for closing modal
 * @param {Function} props.onUpdate - Handler for updating an item
 * @param {Function} props.onDelete - Handler for deleting an item
 * @param {Function} props.onEnterMoveMode - Handler for entering move mode
 * @param {Function} props.onPreview - Handler for previewing content
 * @param {Object} props.formState - Form state object
 * @param {Function} props.handleFormChange - Handler for form changes
 * @param {Function} props.handleContentTypeChange - Handler for content type changes
 * @param {Function} props.handleContentSelection - Handler for content selection
 * @param {Array} props.simulationData - Available simulations
 * @param {Array} props.courseData - Available courses
 * @param {boolean} props.activeModal - Whether this is the active modal
 * @returns {JSX.Element}
 */
const EditItemModal = ({
    open,
    onClose,
    onUpdate,
    onDelete,
    onEnterMoveMode,
    onPreview,
    formState,
    handleFormChange,
    handleContentTypeChange,
    handleContentSelection,
    simulationData,
    courseData,
    activeModal
}) => {
    return (
        <Dialog
            open={open && activeModal !== 'course' && activeModal !== 'simulation'}
            onClose={() => onClose('isEditModalOpen')}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: { borderRadius: MODAL_BORDER_RADIUS },
                sx: {
                    bgcolor: 'background.default'
                }
            }}
            keepMounted
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Edit Item</Typography>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={!formState.contentId}
                        onClick={onPreview}
                        startIcon={<VisibilityIcon />}
                    >
                        Preview
                    </Button>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Content Type Selection */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Content Type</InputLabel>
                            <Select
                                name="contentType"
                                value={formState.contentType}
                                onChange={handleContentTypeChange}
                                label="Content Type"
                                required
                            >
                                <MenuItem value="Simulation">Simulation</MenuItem>
                                <MenuItem value="Course">Course</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Content Selection Dropdown */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Select {formState.contentType}</InputLabel>
                            <Select
                                name="selectedContentName"
                                value={formState.selectedContentName}
                                onChange={handleContentSelection}
                                label={`Select ${formState.contentType}`}
                                required
                            >
                                {formState.contentType === 'Simulation' && simulationData && simulationData.map((sim, index) => (
                                    <MenuItem key={index} value={sim.simulationName}>
                                        {sim.simulationName}
                                    </MenuItem>
                                ))}
                                {formState.contentType === 'Course' && courseData && courseData.map((course, index) => (
                                    <MenuItem key={index} value={course.courseName}>
                                        {course.courseName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Date Selection */}
                    <Grid item xs={12}>
                        <TextField
                            name="scheduledFor"
                            label="Scheduled For"
                            type="date"
                            value={formState.scheduledFor}
                            onChange={handleFormChange}
                            disabled={true}
                            fullWidth
                            required
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onDelete}
                    color="error"
                    startIcon={<DeleteIcon />}
                    sx={{ mr: 'auto' }}
                >
                    Delete
                </Button>

                {/* Cancel button */}
                <Button
                    onClick={() => onClose('isEditModalOpen')}
                    variant="contained"
                    color="warning"
                >
                    Cancel
                </Button>

                {/* Move button */}
                <Button
                    onClick={onEnterMoveMode}
                    variant="contained"
                    color="secondary"
                    startIcon={<DriveFileMoveIcon />}
                >
                    Move Item
                </Button>

                <Button
                    onClick={onUpdate}
                    variant="contained"
                    color="primary"
                    disabled={!formState.contentId || !formState.scheduledFor}
                >
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditItemModal;